import React from 'react';
import { formatNumber, formatMoney } from '../../utils/format';
import { p } from '../../i18n/useP';
import './LcrPlan.css';

/* Plan de calcul du salaire horaire (LCR) — proposition validée le 2026-08-21.
   Il remplace la card « Synthèse » en cinq chiffres alignés, qui montrait le
   RÉSULTAT de chaque étape sans jamais montrer l'OPÉRATION : on lisait cinq
   nombres sans savoir lequel se divisait par lequel.

   UNE SEULE LIGNE de tuiles (retouche du soir — la version à deux branches
   disait le passage TT → TO deux fois : une tuile TT, puis une tuile
   « Passage TT → TO » qui répétait les mêmes heures) :

     rémunération + charges = masse chargée ÷ temps ouvert = taux

   Une tuile = un TITRE et une VALEUR, rien d'autre (retouche du soir) : les
   sous-titres redisaient la valeur (« base 3'227'216 + extras 0 ») ou se
   trompaient (« le coût employeur complet » — la masse chargée est un coût
   PARTIEL : les frais de personnel n'y sont pas). Le détail vit dans le
   tableau, à un clic : chaque tuile y réduit les colonnes à son étape — la
   même grammaire de « focus » que les rapports Finance. Pas de numéros non
   plus : l'équation se lit par ses opérateurs. */

export const LCR_STEPS = ['rem', 'cs', 'masse', 'to', 'lcr'];

export default function LcrPlan({ totals, kpis, unit, year, step, onStep }) {
  const mny = (v) => formatMoney(v, unit, 0);
  const h0 = (v) => formatNumber(v, 0);
  const chf = unit === 'kchf' ? 'kCHF' : 'CHF';

  const to = Number(kpis.total_heures_contractuelles) || 0;

  const Tile = ({ id, label, value, unit: u, tone }) => (
    <button
      type="button"
      className={`lcrp-tile${tone ? ` lcrp-tile--${tone}` : ''}${step === id ? ' is-active' : ''}`}
      onClick={() => onStep(id)}
      aria-pressed={step === id}
    >
      <span className="lcrp-tile__k">{label}</span>
      <span className="lcrp-tile__v">{value}{u && <small>{u}</small>}</span>
      {/* Un chevron seul ne suffisait pas : personne ne devinait que ces tuiles
          ouvraient le détail. Le MOT lève l'ambiguïté, le chevron dit le sens
          (il pivote vers le bas sur la tuile active : le détail est là). */}
      <span className="lcrp-go">{step === id ? p('Détail affiché') : p('Voir le détail')}<i>›</i></span>
    </button>
  );

  return (
    <section className="lcrp" aria-label={p('Calcul du salaire horaire chargé d\'un collaborateur')}>
      <div className="lcrp__cap">
        {p('Calcul du salaire horaire chargé d\'un collaborateur')}
        <small>· {year} · {kpis.nb_personnes} {kpis.nb_personnes > 1 ? p('personnes') : p('personne')}</small>
      </div>

      <div className="lcrp__row">
        <Tile id="rem" label={p('Rémunération')} value={mny(totals.gross_salary)} unit={chf} />
        <span className="lcrp__op lcrp__op--plus">+</span>
        <Tile id="cs" label={p('Charges sociales')} value={mny(totals.social_charges_empr)} unit={chf} />
        <span className="lcrp__op">=</span>
        <Tile id="masse" label={p('Masse salariale chargée')} value={mny(totals.loaded_payroll)} unit={chf} tone="res" />
        <span className="lcrp__op lcrp__op--div">÷</span>
        <Tile id="to" label={p('Temps ouvert (TO)')} value={h0(to)} unit="h" />
        <span className="lcrp__op">=</span>
        <button
          type="button"
          className={`lcrp-res${step === 'lcr' ? ' is-active' : ''}`}
          onClick={() => onStep('lcr')}
          aria-pressed={step === 'lcr'}
        >
          <span className="lcrp-res__k">{p('Salaire horaire moyen')}</span>
          <span className="lcrp-res__v">{formatNumber(kpis.lcr_moyen, 2)}<small>{p('CHF/h')}</small></span>
          <span className="lcrp-go">{step === 'lcr' ? p('Détail affiché') : p('Voir le détail')}<i>›</i></span>
        </button>
      </div>
    </section>
  );
}
